/* Zana, full page: chat history, tabs, @ mentions of buses and a written-out
   answer. Design: ConsoleZanaChat.dc.html. Answers come from the real ask
   (briefingsApi askZana) with the earlier turns as history, shaped into the
   design's card by shapeAnswer. Conversations are kept in this browser
   (localStorage, per school) and nothing else is stored there.
   The jobs she offers under an answer run here (data/zanaExec, 2026-10-28):
   a chip per job, and anything that changes records asks first — the same
   rule as the classic dock (intentNeedsConfirm). They used to send the owner
   to the classic dashboard. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFleet } from '../data/FleetData';
import type { Bus } from '../data/model';
import { shapeAnswer, ZanaSource } from '../data/zana';
import { usePage } from '../Shell';
import { askZana as askZanaApi, AskTurn } from '../../services/briefingsApi';
import { intentNeedsConfirm, isExecutableIntent, labelForZanaIntent, ZanaIntent } from '../../services/zanaActions';
import { useZanaExec } from '../data/zanaExec';
import { useOrgSubscription } from '../../services/useOrgSubscription';
import { ZanaReadButton } from '../ZanaBriefing';
import { ZanaSavingsCard } from '../ZanaSavings';
import { IcX, PageBody, PageSkeleton, ZanaMark, dayMonth, hhmm, plateBeside, useGo, useToast } from '../kit';

/* ── stored shape ── */
interface SAnswer { headline: string; facts: string[]; note: string | null; sources: ZanaSource[]; raw: string; at: number }
interface Turn {
  id: string;
  q: string;
  at: number;
  status: 'pending' | 'done' | 'error' | 'stopped';
  a?: SAnswer;
  cut?: number;          // stopped while writing: how much was shown
  takeover?: boolean;    // the answer offered to do the job on screen (older saved chats)
  jobs?: { intent: ZanaIntent; label: string }[];   // what she offered to do
  did?: { label: string; text: string; ok: boolean }[];
}
interface Chat { id: string; title: string; updated: number; turns: Turn[] }

const META = { key: 'ConsoleZanaChat', title: 'Zana', zanaContext: 'Zana' };
const MAX_CHATS = 50;
const MAX_TURNS = 40;
const MAX_TABS = 5;
const storeKey = (org: string) => `trasse.console.zana.chats.${org}`;

function loadChats(org: string): Chat[] {
  try {
    const raw = localStorage.getItem(storeKey(org));
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list
      .filter((c): c is Chat => !!c && typeof c.id === 'string' && Array.isArray(c.turns))
      .map((c) => ({ ...c, turns: c.turns.map((t) => (t.status === 'pending' ? { ...t, status: 'error' as const } : t)) }));
  } catch { return []; }
}
function saveChats(org: string, chats: Chat[]) {
  try {
    const keep = chats.filter((c) => c.turns.length > 0)
      .sort((a, b) => b.updated - a.updated).slice(0, MAX_CHATS)
      .map((c) => ({ ...c, turns: c.turns.slice(-MAX_TURNS).map((t) => (t.status === 'pending' ? { ...t, status: 'error' as const } : t)) }));
    localStorage.setItem(storeKey(org), JSON.stringify(keep));
  } catch { /* storage blocked or full: chats live for this visit only */ }
}

const newId = (p: string) => p + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const plain = (a: SAnswer) => [a.headline, ...a.facts.map((x) => '• ' + x), a.note].filter(Boolean).join('\n');
const STEPS = ['Reading your buses and records', 'Checking what changed recently', 'Writing the answer'];

/* icons from the design's set */
const P = {
  bus: <><rect x="4" y="3" width="16" height="15" rx="3" /><path d="M4 11h16M8 21v-3M16 21v-3" /><circle cx="8" cy="14.5" r=".6" /><circle cx="16" cy="14.5" r=".6" /></>,
  fuel: <><path d="M3 22V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v17" /><path d="M3 22h12" /><path d="M15 10h2a2 2 0 0 1 2 2v5a2 2 0 0 0 4 0V9l-3-3" /><path d="M7 7h4v4H7z" /></>,
  wrench: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" />,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
  users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6.5 6.5 0 0 1 3.5 6" /></>,
  arrow: <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>,
};
const Ico: React.FC<{ d: React.ReactNode; size?: number; sw?: string }> = ({ d, size = 13, sw = '1.75' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>{d}</svg>
);
const srcIcon = (page: string) => (page === 'ConsoleBuses' || page === 'ConsoleLiveMap' ? P.bus : page === 'ConsoleFuel' ? P.fuel : page === 'ConsoleMaintenance' ? P.wrench : page === 'ConsoleAlerts' ? P.bell : page === 'ConsolePeople' ? P.users : P.file);
const srcKind = (page: string) => (page === 'ConsoleBuses' ? 'Bus' : page === 'ConsoleFuel' ? 'Fuel log' : page === 'ConsoleMaintenance' ? 'Maintenance' : page === 'ConsoleAlerts' ? 'Alerts' : page === 'ConsoleLiveMap' ? 'Live map' : 'Page');

function whenLabel(ms: number): string {
  const d = new Date(ms);
  if (d.toDateString() === new Date().toDateString()) return hhmm(d);
  if (Date.now() - ms < 6 * 86400000) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return dayMonth(d);
}
function groupOf(ms: number): string {
  const d = new Date(ms); const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const y = new Date(today); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return Date.now() - ms < 7 * 86400000 ? 'This week' : 'Earlier';
}
const norm = (s: string) => s.toUpperCase().replace(/\s+/g, '');

const LOCAL_CSS = `
.tc .zc-root{display:flex;margin:-28px -32px -48px;height:calc(100% + 76px);min-height:520px;align-items:stretch}
.tc .zc-root textarea{resize:none}
@keyframes zc-caret{0%,49%{opacity:1}50%,100%{opacity:0}}
.tc .zc-caret{display:inline-block;width:8px;height:18px;margin-left:2px;vertical-align:-3px;background:var(--z);border-radius:2px;animation:zc-caret 900ms steps(1) infinite}
@media (prefers-reduced-motion: reduce){.tc .zc-caret{animation:none}}
@container tc (max-width: 820px){.tc .zc-root{margin:-20px -16px -40px;height:calc(100% + 60px)}}`;

const ZanaChat: React.FC = () => {
  const f = useFleet();
  const zanaTier = useOrgSubscription(f.org.code || null).isZanaTier;
  const go = useGo();
  const { toast } = useToast();
  usePage({
    // The sidebar folds to its icons, as on the live map, so the chat has the screen (Gabriel, 2026-10-29).
    key: 'ConsoleZanaChat', title: 'Zana', zanaContext: 'Zana · full page', rail: true,
    zanaIntro: 'Ask me anything about your buses, drivers, fuel and trips. I answer from your own records.',
    prompts: [],
  });

  const org = f.org.code;
  const [chats, setChats] = useState<Chat[]>([]);
  const [open, setOpen] = useState<string[]>([]);
  const [active, setActive] = useState('');
  const [leaving, setLeaving] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [at, setAt] = useState<{ q: string; start: number } | null>(null);
  const [atIdx, setAtIdx] = useState(0);
  const [pending, setPending] = useState<{ turnId: string; step: number } | null>(null);
  const [stream, setStream] = useState<{ turnId: string; n: number; len: number } | null>(null);

  const loaded = useRef('');
  const runRef = useRef(0);
  const fRef = useRef(f); fRef.current = f;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const later = useCallback((fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); }, []);
  useEffect(() => () => { timers.current.forEach(clearTimeout); runRef.current++; }, []);

  /* load this school's chats once its code is known; open the latest */
  useEffect(() => {
    if (!org || loaded.current === org) return;
    loaded.current = org;
    const list = loadChats(org).sort((a, b) => b.updated - a.updated);
    const fresh: Chat = { id: newId('n'), title: 'New chat', updated: Date.now(), turns: [] };
    setChats(list.concat([fresh]));
    setOpen(list[0] ? [list[0].id, fresh.id] : [fresh.id]);
    setActive(fresh.id);
  }, [org]);
  useEffect(() => { if (org && loaded.current === org) saveChats(org, chats); }, [org, chats]);

  const chat = chats.find((c) => c.id === active) || null;
  const busy = !!pending || !!stream;

  /* named steps while she works (UI only; three steps, then it waits) */
  const pendingId = pending?.turnId;
  useEffect(() => {
    if (!pendingId) return undefined;
    const bump = (n: number) => setPending((p) => (p && p.turnId === pendingId ? { ...p, step: Math.max(p.step, n) } : p));
    const a = setTimeout(() => bump(1), 900);
    const b = setTimeout(() => bump(2), 1900);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [pendingId]);

  /* write the answer out; ends when the text is all shown */
  useEffect(() => {
    if (!stream) return undefined;
    if (stream.n >= stream.len) { setStream(null); return undefined; }
    const t = setTimeout(() => setStream((s) => (s ? { ...s, n: s.n + Math.max(7, Math.ceil(s.len / 60)) } : s)), 40);
    return () => clearTimeout(t);
  }, [stream]);

  /* keep the newest turn in view */
  const turnsLen = chat?.turns.length || 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [active, turnsLen, stream?.n, pending?.step]);

  const patchTurn = useCallback((chatId: string, turnId: string, patch: Partial<Turn>) => {
    setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, updated: Date.now(), turns: c.turns.map((t) => (t.id === turnId ? { ...t, ...patch } : t)) })));
  }, []);

  const runAsk = useCallback(async (chatId: string, turnId: string, q: string, history: AskTurn[]) => {
    const token = ++runRef.current;
    setPending({ turnId, step: 0 });
    const fl = fRef.current;
    try {
      if (!fl.org.code) throw new Error('no org');
      const mentioned = fl.buses.filter((b) => b.plate && norm(q).includes(norm(b.plate)));
      const unread = fl.alerts.filter((x) => !x.read);
      const res = await askZanaApi(
        fl.org.code, q,
        {
          page: 'zana',
          ...(mentioned.length === 1 ? { entityType: 'bus' as const, entityId: mentioned[0].id } : {}),
          cards: [
            { label: 'Fleet buses', value: String(fl.buses.length) },
            { label: 'Active buses', value: String(fl.buses.filter((b) => b.status === 'on_route' || b.status === 'late').length) },
          ],
          focus: (mentioned.length ? mentioned : fl.buses.filter((b) => b.needsLook)).slice(0, 12).map((b) => ({ type: 'bus' as const, id: b.id })),
        },
        history,
        {
          unreadAlertCount: unread.length,
          highRiskBusCount: new Set(unread.filter((x) => x.severity === 'urgent').map((x) => x.busId).filter(Boolean)).size,
          unreadBusIds: unread.map((x) => x.busId).filter((x): x is string => !!x),
        },
      );
      if (token !== runRef.current) return;
      if (!res.ok || !res.answer) throw new Error(res.message || 'no answer');
      const s = shapeAnswer(res.answer, fl, META);
      const a: SAnswer = { headline: s.headline, facts: s.facts, note: s.note, sources: s.sources, raw: s.raw, at: Date.now() };
      /* Her jobs: the allow-listed intents, plus a walkthrough she offered.
         A walkthrough drives the classic screens, so here it opens the page. */
      const jobs = (res.intents || []).filter((i) => isExecutableIntent(i as ZanaIntent))
        .map((i) => ({ intent: i as ZanaIntent, label: labelForZanaIntent(i as ZanaIntent) }));
      if (res.takeover && (res.takeover.scriptId || res.takeover.topic) && !jobs.some((j) => j.intent.actionId === 'start_takeover')) {
        jobs.push({ intent: { actionId: 'start_takeover', params: { scriptId: res.takeover.scriptId, topic: res.takeover.topic } }, label: 'Show me where' });
      } else if (res.takeoverOffer?.topic && !jobs.some((j) => j.intent.actionId === 'start_takeover')) {
        jobs.push({ intent: { actionId: 'start_takeover', params: { topic: res.takeoverOffer.topic } }, label: 'Show me where' });
      }
      patchTurn(chatId, turnId, { status: 'done', a, jobs: jobs.slice(0, 4), did: [] });
      setPending(null);
      setStream({ turnId, n: 0, len: plain(a).length });
    } catch (e) {
      if (token !== runRef.current) return;
      // eslint-disable-next-line no-console
      console.warn('[console] Zana could not answer', e);
      patchTurn(chatId, turnId, { status: 'error' });
      setPending(null);
    }
  }, [patchTurn]);

  const historyOf = (c: Chat, beforeId?: string): AskTurn[] => {
    const out: AskTurn[] = [];
    for (const t of c.turns) {
      if (t.id === beforeId) break;
      if (t.status === 'done' && t.a) out.push({ from: 'you', text: t.q }, { from: 'zana', text: t.a.raw });
    }
    return out.slice(-12);
  };

  const send = (text?: string) => {
    const q = (text ?? draft).trim();
    if (!q || busy) return;
    let c = chat;
    if (!c) {
      c = { id: newId('n'), title: 'New chat', updated: Date.now(), turns: [] };
      const nc = c;
      setOpen((o) => o.concat([nc.id]).slice(-MAX_TABS));
      setActive(nc.id);
    }
    const turn: Turn = { id: newId('t'), q, at: Date.now(), status: 'pending' };
    const history = historyOf(c);
    const base = c;
    setChats((cs) => {
      const has = cs.some((x) => x.id === base.id);
      const upd = (x: Chat): Chat => ({ ...x, title: x.turns.length ? x.title : q.slice(0, 60), updated: Date.now(), turns: x.turns.concat([turn]) });
      return has ? cs.map((x) => (x.id === base.id ? upd(x) : x)) : cs.concat([upd(base)]);
    });
    if (text === undefined) { setDraft(''); setAt(null); }
    void runAsk(base.id, turn.id, q, history);
  };

  const retry = (t: Turn) => {
    if (!chat || busy) return;
    patchTurn(chat.id, t.id, { status: 'pending', a: undefined, cut: undefined, takeover: undefined, jobs: undefined, did: undefined });
    void runAsk(chat.id, t.id, t.q, historyOf(chat, t.id));
  };

  /* ── her jobs: confirm a write, run it, say what happened ── */
  const exec = useZanaExec();
  const [asking, setAsking] = useState<{ turnId: string; i: number } | null>(null);
  const [running, setRunning] = useState<{ turnId: string; i: number } | null>(null);
  const runJob = (t: Turn, i: number, confirmed = false) => {
    const job = t.jobs?.[i];
    if (!chat || !job || running) return;
    if (!confirmed && intentNeedsConfirm(job.intent)) { setAsking({ turnId: t.id, i }); return; }
    setAsking(null);
    setRunning({ turnId: t.id, i });
    const chatId = chat.id;
    Promise.resolve(exec(job.intent))
      .then((out) => ({ label: job.label, text: out || '', ok: true }))
      .catch((e: Error) => ({ label: job.label, text: e?.message || 'That didn’t work. Try again in a minute.', ok: false }))
      .then((line) => {
        if (line.text) setChats((cs) => cs.map((c) => (c.id !== chatId ? c : { ...c, turns: c.turns.map((x) => (x.id === t.id ? { ...x, did: (x.did || []).concat([line]) } : x)) })));
      })
      .finally(() => setRunning(null));
  };

  const stop = () => {
    if (!chat) return;
    if (pending) { runRef.current++; patchTurn(chat.id, pending.turnId, { status: 'stopped' }); setPending(null); }
    else if (stream) { patchTurn(chat.id, stream.turnId, { cut: Math.min(stream.n, stream.len) }); setStream(null); }
  };

  /* ── tabs ── */
  const titleOf = (id: string) => chats.find((c) => c.id === id)?.title || 'New chat';
  const focusTab = (id: string) => later(() => document.getElementById(`ztab-${id}`)?.focus(), 30);
  const newChat = () => {
    const c: Chat = { id: newId('n'), title: 'New chat', updated: Date.now(), turns: [] };
    setChats((cs) => cs.concat([c]));
    setOpen((o) => o.concat([c.id]).slice(-MAX_TABS));
    setActive(c.id);
    later(() => inputRef.current?.focus(), 30);
  };
  const openChat = (id: string) => {
    setOpen((o) => (o.includes(id) ? o : o.concat([id]).slice(-MAX_TABS)));
    setActive(id);
  };
  const closeTab = (id: string) => {
    if (leaving) return;
    const title = titleOf(id);
    const idx = open.indexOf(id);
    setLeaving(id);
    later(() => {
      const rest = open.filter((x) => x !== id);
      let auto = '';
      if (!rest.length) {
        const c: Chat = { id: newId('n'), title: 'New chat', updated: Date.now(), turns: [] };
        auto = c.id;
        setChats((cs) => cs.concat([c]));
        rest.push(c.id);
      }
      const next = active === id ? (rest[idx] || rest[idx - 1] || rest[0]) : active;
      setOpen(rest);
      setActive(next);
      setLeaving('');
      focusTab(next);
      toast({
        kind: 'info', text: `Closed “${title}”`, actLabel: 'Undo', ms: 5000,
        onAct: () => {
          setOpen((o) => { const n = o.filter((x) => x !== id && x !== auto); n.splice(Math.min(idx, n.length), 0, id); return n.slice(-MAX_TABS); });
          setActive(id);
          focusTab(id);
        },
      });
    }, 160);
  };

  /* ── @ mentions ── */
  const atList = useMemo<Bus[]>(() => {
    if (!at) return [];
    const q = norm(at.q);
    return f.buses.filter((b) => !q || norm(b.plate).includes(q) || norm(b.name).includes(q)).slice(0, 8);
  }, [at, f.buses]);
  const onDraft = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setDraft(v);
    const upto = v.slice(0, e.target.selectionStart ?? v.length);
    const m = upto.match(/(^|\s)@([\w-]*)$/);
    if (m) { setAt({ q: m[2], start: upto.length - m[2].length - 1 }); setAtIdx(0); } else setAt(null);
  };
  const pick = (b: Bus) => {
    if (!at) return;
    const end = at.start + 1 + at.q.length;
    const ins = `${b.plate} `;
    const v = draft.slice(0, at.start) + ins + draft.slice(end);
    const caret = at.start + ins.length;
    setDraft(v); setAt(null);
    later(() => { const el = inputRef.current; if (el) { el.focus(); el.setSelectionRange(caret, caret); } }, 0);
  };
  const startMention = () => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? draft.length;
    const pre = draft.slice(0, pos);
    const sp = pre && !/\s$/.test(pre) ? ' ' : '';
    const v = pre + sp + '@' + draft.slice(pos);
    const caret = pos + sp.length + 1;
    setDraft(v); setAt({ q: '', start: caret - 1 }); setAtIdx(0);
    later(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } }, 0);
  };
  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (at && atList.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAtIdx((i) => (i + 1) % atList.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAtIdx((i) => (i - 1 + atList.length) % atList.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(atList[Math.min(atIdx, atList.length - 1)]); return; }
    }
    if (at && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAt(null); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };
  const mentions = useMemo(() => f.buses.filter((b) => b.plate && norm(draft).includes(norm(b.plate))), [draft, f.buses]);

  const copy = (a: SAnswer) => {
    const done = () => toast({ kind: 'success', text: 'Answer copied' });
    const fail = () => toast({ kind: 'error', text: 'Couldn’t copy. Select the text and copy it instead.' });
    try { void navigator.clipboard.writeText(plain(a)).then(done, fail); } catch { fail(); }
  };

  /* ── history list ── */
  const history = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats.filter((c) => c.turns.length > 0)
      .filter((c) => !q || c.title.toLowerCase().includes(q) || c.turns.some((t) => t.q.toLowerCase().includes(q)))
      .sort((a, b) => b.updated - a.updated);
  }, [chats, search]);
  const groups: { name: string; list: Chat[] }[] = [];
  history.forEach((c) => {
    const g = groupOf(c.updated);
    const last = groups[groups.length - 1];
    if (last && last.name === g) last.list.push(c); else groups.push({ name: g, list: [c] });
  });

  /* ── records read in this chat ── */
  const ctx = useMemo(() => {
    const seen = new Set<string>();
    const out: (ZanaSource & { k: string })[] = [];
    chat?.turns.forEach((t) => t.a?.sources.forEach((s) => {
      const key = s.page + (s.search || '') + s.label;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ ...s, k: srcKind(s.page) });
    }));
    return out;
  }, [chat]);

  /* starter prompts, from live records */
  const starters = useMemo(() => {
    const thirsty = f.buses.filter((b) => b.kmpl != null && b.kmplNormal != null && b.kmpl < b.kmplNormal * 0.9)
      .sort((a, b) => (a.kmpl as number) / (a.kmplNormal as number) - (b.kmpl as number) / (b.kmplNormal as number))[0];
    const late = f.buses.find((b) => b.status === 'late');
    return [
      { icon: P.fuel, q: thirsty ? `Why is ${thirsty.plate} burning more fuel?` : 'Which bus is using the most fuel?' },
      { icon: P.users, q: late ? `Why is ${late.name} late?` : 'Who wasn’t picked up today?' },
      { icon: P.file, q: 'Summarise this week for the head teacher' },
    ];
  }, [f.buses]);

  const renderTurn = (t: Turn) => {
    const streaming = stream?.turnId === t.id;
    const isPending = t.status === 'pending' && pending?.turnId === t.id;
    const a = t.a;
    return (
      <div key={t.id} className="up" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div className="zq" style={{ maxWidth: '78%', whiteSpace: 'pre-wrap' }}>{t.q}</div>

        {isPending && (
          <div className="zcard zin-a" style={{ gap: '8px' }} role="status" aria-live="polite">
            {STEPS.map((s, i) => {
              const step = pending?.step || 0;
              const color = i < step ? 'var(--ok)' : i === step ? 'var(--z-ink)' : 'var(--faint)';
              return (
                <div key={s} className="row" style={{ gap: '10px', fontSize: '14px', color }}>
                  <span style={{ width: '18px', display: 'inline-flex', justifyContent: 'center' }}>{i < step ? '✓' : i === step ? '●' : '○'}</span>
                  {s}{i === step ? '…' : ''}
                </div>
              );
            })}
          </div>
        )}

        {(t.status === 'error' || (t.status === 'pending' && !isPending)) && (
          <div className="zcard zin-a" style={{ borderColor: 'rgba(194,59,59,.25)' }} role="alert">
            <div className="row" style={{ gap: '8px', color: 'var(--bad)', fontWeight: 600 }}>
              <Ico size={17} d={<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></>} />
              Zana couldn’t answer
            </div>
            <p className="sm" style={{ margin: 0, color: 'var(--ink2)' }}>Your question is saved. This usually clears in a minute.</p>
            <div className="zacts">
              <button type="button" className="btn s b-ink" disabled={busy} onClick={() => retry(t)}>Try again</button>
            </div>
          </div>
        )}

        {t.status === 'stopped' && (
          <div className="row" style={{ gap: 8 }}>
            <span className="chip c-mute">Stopped before she answered.</span>
            <button type="button" className="btn s b-ghost" disabled={busy} onClick={() => retry(t)}>Ask again</button>
          </div>
        )}

        {t.status === 'done' && a && streaming && (
          <p style={{ margin: '0', fontSize: '16px', lineHeight: '26px', whiteSpace: 'pre-wrap' }} aria-busy="true">
            {plain(a).slice(0, stream?.n || 0)}
            <span className="zc-caret" aria-hidden="true" />
          </p>
        )}

        {t.status === 'done' && a && !streaming && (
          <div className="zin-a" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {t.cut != null ? (
              <>
                <p style={{ margin: '0', fontSize: '16px', lineHeight: '26px', whiteSpace: 'pre-wrap' }}>{plain(a).slice(0, t.cut)}</p>
                <span className="chip c-mute" style={{ alignSelf: 'flex-start' }}>Stopped. What she wrote so far is kept.</span>
              </>
            ) : (
              <div className="zcard" style={{ border: '0', padding: '0', gap: '12px' }}>
                <p style={{ margin: '0', fontSize: '16px', lineHeight: '23px', fontWeight: 600 }}>{a.headline}</p>
                {a.facts.length > 0 && <ul className="zfacts">{a.facts.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                {a.note && <p className="sm" style={{ margin: '0', color: 'var(--muted)' }}>{a.note}</p>}
                {a.sources.length > 0 && (
                  <div className="zsrc">
                    {a.sources.map((s, i) => <a key={i} {...go(s.page, s.search || '')}><Ico d={srcIcon(s.page)} />{s.label}</a>)}
                  </div>
                )}
              </div>
            )}
            {(t.did || []).map((d, i) => (
              <p key={i} className="sm" role="status" style={{ margin: 0, color: d.ok ? 'var(--ink2)' : 'var(--bad)', display: 'flex', alignItems: 'flex-start', gap: 8, whiteSpace: 'pre-wrap' }}>
                <span style={{ flex: 'none', marginTop: 2 }}><ZanaMark /></span>
                <span><b>{d.label}.</b> {d.text}</span>
              </p>
            ))}
            {t.jobs && t.jobs.length > 0 && chat?.turns[chat.turns.length - 1]?.id === t.id && (
              asking?.turnId === t.id ? (
                <div className="zcard" style={{ gap: 10 }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>{t.jobs[asking.i]?.label}?</p>
                  <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>This changes your records or sends something out. Zana does it once you say so.</p>
                  <div className="zacts">
                    <button type="button" className="btn s b-ink" onClick={() => runJob(t, asking.i, true)}>Do it</button>
                    <button type="button" className="btn s b-line" onClick={() => setAsking(null)}>Not now</button>
                  </div>
                </div>
              ) : (
                <div className="zacts" style={{ flexWrap: 'wrap' }}>
                  {t.jobs.map((j, i) => (
                    <button key={i} type="button" className="btn s b-line" disabled={!!running} onClick={() => runJob(t, i)}
                      style={{ borderColor: 'var(--z-line)', color: 'var(--z-ink)' }}>
                      {running?.turnId === t.id && running.i === i ? 'Working…' : j.label}
                    </button>
                  ))}
                </div>
              )
            )}
            <div className="row" style={{ gap: '2px', color: 'var(--faint)', fontSize: '13px' }}>
              <span>{whenLabel(a.at)} · from your Trasse records</span>
              <span style={{ flex: '1' }} />
              <button type="button" className="ibtn" style={{ width: '32px', height: '32px' }} aria-label="Copy" onClick={() => copy(a)}>
                <Ico size={16} d={<><rect x="8" y="8" width="13" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>} />
              </button>
              <button type="button" className="ibtn" style={{ width: '32px', height: '32px' }} aria-label="Ask again" disabled={busy} onClick={() => send(t.q)}>
                <Ico size={16} d={P.arrow} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats />}>
      <div className="zc-root">
        <style>{LOCAL_CSS}</style>

        <aside style={{ width: '264px', flex: 'none', borderRight: '1px solid var(--line)', padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: '4px', background: 'var(--s1)', overflowY: 'auto' }} className="desk" aria-label="Chat history">
          <div className="row" style={{ gap: '8px', padding: '0 4px 8px' }}>
            <b style={{ fontSize: '18px', fontFamily: "'Barlow Semi Condensed', Arial, sans-serif", flex: '1' }}>Zana</b>
            <button type="button" className="btn s b-ink" onClick={newChat}><Ico size={15} d={<path d="M12 5v14M5 12h14" />} />New chat</button>
          </div>
          <div className="zinput" style={{ height: '38px', margin: '0 4px' }}>
            <Ico size={15} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
            <input id="chat-search" type="text" placeholder="Search chats" aria-label="Search chats" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {history.length === 0 && (
            <p className="sm" style={{ margin: '14px 10px', color: 'var(--muted)' }}>
              {search.trim() ? <>No chat matches “{search.trim()}”. Try a bus plate or a word from your question. <button type="button" className="btn s b-ghost" onClick={() => setSearch('')}>Clear search</button></> : 'Your chats with Zana appear here.'}
            </p>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.name}>
              <p className="mono" style={{ margin: '14px 10px 6px', fontSize: '11px', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--faint)' }}>{g.name}</p>
              {g.list.map((c) => (
                <button key={c.id} type="button" className={`nav ${c.id === active ? 'on' : ''}`} aria-current={c.id === active ? 'true' : undefined} onClick={() => openChat(c.id)}
                  style={{ width: '100%', border: '0', textAlign: 'left', cursor: 'pointer', height: 'auto', padding: '8px 10px', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ flex: '1', minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{c.title}</span>
                  <span style={{ fontSize: '12px', opacity: '.7', flex: 'none' }}>{whenLabel(c.updated)}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </aside>

        <section style={{ flex: '1 1 480px', minWidth: '0', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }} aria-label="Conversation">
          <div className="row" role="tablist" aria-label="Open chats" style={{ gap: '2px', padding: '8px 12px 0', borderBottom: '1px solid var(--line)', overflowX: 'auto' }}>
            {open.map((id) => (
              <div key={id} className={'ztab' + (id === active ? ' on' : '') + (id === leaving ? ' out' : '')} role="tab" aria-selected={id === active}>
                <button type="button" id={`ztab-${id}`} className="ztab-l" onClick={() => setActive(id)}>{titleOf(id)}</button>
                <button type="button" className="ztab-x" aria-label={`Close chat “${titleOf(id)}”`} onClick={() => closeTab(id)}><IcX size={14} /></button>
              </div>
            ))}
            <button type="button" className="ibtn" aria-label="New chat" onClick={newChat} style={{ width: '32px', height: '32px', marginBottom: '4px' }}>
              <Ico size={16} d={<path d="M12 5v14M5 12h14" />} />
            </button>
            {zanaTier && <ZanaReadButton orgCode={f.org.code} />}
          </div>

          <div ref={scrollRef} style={{ flex: '1', overflow: 'auto', padding: '28px 24px 8px' }}>
            <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {chat && chat.turns.length > 0 ? chat.turns.map(renderTurn) : (
                <div className="up" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', padding: '72px 0 24px' }}>
                  <span aria-hidden="true" style={{ width: '44px', height: '44px', borderRadius: '14px', background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flex: 'none' }}>
                    <i style={{ width: '7px', height: '11px', borderRadius: '3px', background: '#fff', display: 'block' }} />
                    <i style={{ width: '7px', height: '11px', borderRadius: '3px', background: '#fff', display: 'block' }} />
                  </span>
                  <h2 className="h2" style={{ fontSize: '26px' }}>What can I help with?</h2>
                  <p className="sub" style={{ maxWidth: '420px' }}>I read your buses, trips, fuel and alerts. I can explain a number, find a bus or a child, or draft a message.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', width: '100%', maxWidth: '560px', marginTop: '8px' }}>
                    {starters.map((s) => (
                      <button key={s.q} type="button" className="zp-prompt" disabled={busy} onClick={() => send(s.q)}>
                        <span style={{ color: 'var(--z)' }}><Ico size={16} d={s.icon} /></span>{s.q}
                      </button>
                    ))}
                    <button type="button" className="zp-prompt" disabled={busy} onClick={() => send('What jobs can you do for me?')}>
                      <span style={{ color: 'var(--z)' }}><Ico size={16} d={P.arrow} /></span>What jobs can you do for me?
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: '12px 24px 20px', position: 'relative' }}>
            {at && (
              <div className="pal" id="zc-mentions" style={{ position: 'absolute', left: '24px', bottom: 'calc(100% - 4px)', width: 'min(340px, calc(100% - 48px))', animation: 'pop 150ms cubic-bezier(0,0,.2,1) both' }}>
                <div className="pal-g" role="listbox" aria-label="Buses">
                  <p>Buses</p>
                  {atList.length === 0 ? (
                    <span className="sm" style={{ display: 'block', padding: '6px 10px 10px', color: 'var(--muted)' }}>{f.buses.length ? `No bus matches “${at.q}”. Type part of a plate or route name.` : 'No buses yet.'}</span>
                  ) : atList.map((b, i) => (
                    <button key={b.id} type="button" role="option" aria-selected={i === atIdx} className={'pal-i' + (i === atIdx ? ' hi' : '')}
                      onMouseDown={(e) => e.preventDefault()} onClick={() => pick(b)}>
                      <span><b>{b.name}</b>{plateBeside(b) ? <> <span className="mono">{b.plate}</span></> : null}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ border: '1px solid var(--line2)', borderRadius: '14px', background: 'var(--s1)', padding: '10px 10px 10px 14px', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 1px 2px rgba(17,21,32,.06)' }}>
              {mentions.length > 0 && (
                <div className="row" style={{ gap: '6px', flexWrap: 'wrap' }}>
                  {mentions.map((b) => <span key={b.id} className="chip c-z" style={{ height: '26px' }}>@ {b.plate}</span>)}
                </div>
              )}
              <textarea ref={inputRef} id="zana-full-ask" rows={Math.min(6, Math.max(1, draft.split('\n').length))} placeholder="Ask Zana, type @ for a bus" aria-label="Ask Zana"
                aria-controls={at ? 'zc-mentions' : undefined}
                value={draft} onChange={onDraft} onKeyDown={onKey} onBlur={() => later(() => setAt(null), 150)}
                style={{ border: '0', outline: '0', font: 'inherit', fontSize: '16px', lineHeight: '24px', background: 'transparent', color: 'var(--ink)', minHeight: '28px', padding: 0 }} />
              <div className="row" style={{ gap: '4px' }}>
                <button type="button" className="ibtn" aria-label="Mention a bus" onClick={startMention} style={{ width: '32px', height: '32px', fontWeight: 700 }}>@</button>
                <span className="sm desk" style={{ color: 'var(--faint)', marginLeft: '6px' }}>Enter to send · Shift+Enter for a new line</span>
                <span style={{ flex: '1' }} />
                {busy ? (
                  <button type="button" className="btn s b-line" onClick={stop}>Stop</button>
                ) : (
                  <button type="button" className="btn s b-ink" onClick={() => send()} aria-label="Send" disabled={!draft.trim()} style={{ width: '34px', padding: '0' }}>
                    <Ico size={16} d={<path d="M5 12h14M13 6l6 6-6 6" />} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <aside style={{ width: '300px', flex: 'none', borderLeft: '1px solid var(--line)', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }} className="desk" aria-label="In this chat">
          {zanaTier && <ZanaSavingsCard orgCode={f.org.code} />}
          <b style={{ fontSize: '15px' }}>In this chat</b>
          <p className="sm" style={{ margin: '0', color: 'var(--muted)' }}>{ctx.length ? 'Records Zana pointed to in her answers.' : 'Records Zana points to in her answers will show here.'}</p>
          {ctx.map((cx, i) => (
            <a key={i} {...go(cx.page, cx.search || '')} className="row" style={{ gap: '10px', padding: '10px', border: '1px solid var(--line)', borderRadius: '10px' }}>
              <span className="av" style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'var(--z-tint)', color: 'var(--z-ink)' }}>
                <Ico size={15} sw="1.9" d={srcIcon(cx.page)} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: '0' }}>
                <b style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cx.label}</b>
                <span className="sm" style={{ color: 'var(--muted)' }}>{cx.k}</span>
              </span>
            </a>
          ))}
          <div style={{ flex: '1' }} />
          <div className="panel" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <b className="sm" style={{ fontSize: '13px' }}>Your chats stay on this computer</b>
            <span className="sm" style={{ color: 'var(--muted)' }}>Saved in this browser for {f.org.name || 'your school'}, last {MAX_CHATS} chats. Other admins can’t see them. Clearing browser data removes them.</span>
          </div>
        </aside>
      </div>
    </PageBody>
  );
};

export default ZanaChat;
